import React, { useCallback, useEffect, useMemo, useState } from 'react';
import MobileModal from '../../../../components/compositions/MobileModal/MobileModal';
import moment from 'moment';
import AppError from '../../../../apis/models/common/res/AppError';
import useAuthStore from '../../../../store/useAuthStore';
import Button from '../../../../components/atom/Button';
import strings from '../../../../constants/strings.constants';
import colors from '../../../../constants/colors.constants';
import Typo from '../../../../components/atom/Typo';
import {
  addReservation,
  getUtmQueries,
  sendReservationCompleteMessage,
} from '../../../../apis/reservations';
import { AddReservationBody } from '../../../../apis/models/reservations/req/AddReservationBody';
import { ROUTER_PATHS } from '../../../../routes/routes.constatns';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ReservationChannel } from '../../../../apis/models/reservations/res/Reservation';
import {
  getEarlyAvailableDateWhenDisabled,
  getPartnerName,
  isValidReservationChannel,
  isValidReservationPushChannel,
} from '../../../../utils/utils';
import useAlertStore from '../../../../store/useAlertStore';
import useLanguageCodeStore, {
  LanguageCode,
} from '../../../../store/useLanguageCodeStore';
import AuthBottomSheet from '../../../../components/compositions/AuthBottomSheet/AuthBottomSheet';
import ReservationCalendar from '../../../../components/compositions/ReservationCalendar';
import ReservationTimeSelectRadioGroup from '../../../../components/compositions/ReservationTimeSelectRadioGroup';
import { useQuery } from 'react-query';
import {
  fetchAvailableReservationDates,
  fetchAvailableReservationTimes,
} from '../../../../apis/partner-schedules';
import { PartnerScheduleTime } from '../../../../apis/models/partner-schedules/res/PartnerScheduleTime';
import { PartnerScheduleAvailableDate } from '../../../../apis/models/partner-schedules/res/PartnerScheduleAvailableDate';
import { ChatbotChannel } from '../../../../apis/models/auth/req/ChatbotGuestSignInBody';
import URL_QUERY_KEYS from '../../../../constants/urlQueryKeys.constants';
import { sendPurchaseEvent } from '../../../../utils/ga-utils';

export interface ReservationConfirmModalProps {
  onCancel: () => void;
  visible?: boolean;
  selectedDate?: Date | null;
  selectedTime?: string;
  treatmentCategoryId?: number;
  consultingMemo?: string;
  consultingCategories?: string[];
  isConsultingReservation?: boolean;
  parentCategoryName?: string;
  childCategoryName?: string;
  reservationType: 'CONSULTING' | 'REVISIT' | 'TREATMENT';
}

function ReservationConfirmModal(props: ReservationConfirmModalProps) {
  const {
    visible = false,
    onCancel,
    treatmentCategoryId,
    isConsultingReservation = false,
    parentCategoryName,
    childCategoryName,
  } = props;
  const location = useLocation();
  const { partner, customer, channel } = useAuthStore();
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const { showAlert } = useAlertStore();
  const { languageCode } = useLanguageCodeStore();
  const [authModalVisible, setAuthModalVisible] = useState(false);
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [currentCalendarVisibleMonth, setCurrentCalendarVisibleMonth] =
    useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [timeBlocks, setTimeBlocks] = useState<PartnerScheduleTime[]>([]);
  const [selectedTime, setSelectedTime] = useState<string>();
  const [availableDates, setAvailableDates] = useState<
    Record<string, PartnerScheduleAvailableDate>
  >({});
  const [autoSelectDateErrorTimes, setAutoSelectDateErrorTimes] = useState(0);
  const { consultingCategories = [], consultingMemo, reservationType } = props;
  const customLabel =
    partner?.productViewConfig?.mimoWebViewConfig?.customLabel;

  const isConsultingReservationAutoAccept = useMemo(() => {
    if (partner) {
      const { homepageConfig } = partner;
      return !!homepageConfig?.consultingReservationAutoAccept;
    }
    return false;
  }, [partner]);

  const isTreatmentReservationAutoAccept = useMemo(() => {
    if (partner) {
      const { homepageConfig } = partner;
      return !!homepageConfig?.treatmentReservationAutoAccept;
    }
    return false;
  }, [partner]);

  const reservationAvailableDatesQuery = useQuery({
    queryKey: [
      moment(currentCalendarVisibleMonth).format('YYYY-MM'),
      treatmentCategoryId,
    ],
    queryFn: async () => {
      const momentDate = moment(currentCalendarVisibleMonth);
      const year = momentDate.get('year');
      const month = momentDate.get('month') + 1;

      return fetchAvailableReservationDates({
        year,
        month,
        partnerCode: partner?.partnerCode || '',
        isConsultingReservation: true,
      });
    },
    onSuccess: (data) => {
      setAvailableDates(data);
      if (selectedDate) {
        const updatedDate = getEarlyAvailableDateWhenDisabled(
          selectedDate,
          data,
        );
        if (updatedDate) {
          setSelectedDate(updatedDate);
        }
      }
    },
    retry: false,
    enabled: !!partner && visible,
    refetchOnWindowFocus: false,
  });

  const reservationTimeBlocksQuery = useQuery({
    queryKey: [
      moment(selectedDate).format('YYYY-MM-DD'),
      { partnerCustomerId: customer?.partnerCustomerId, treatmentCategoryId },
    ],
    queryFn: async () => {
      const dateParam = moment(selectedDate).format('YYYY-MM-DD');

      return fetchAvailableReservationTimes({
        targetDate: dateParam,
        partnerCode: partner?.partnerCode || '',
        isConsultingReservation: true,
        treatmentCategoryId,
        partnerCustomerId: customer?.partnerCustomerId,
      });
    },
    onSuccess: (data) => {
      setTimeBlocks(data);
    },
    onError: (error) => {
      const appError = error as AppError;
      if (appError.statusCode === 400 && autoSelectDateErrorTimes <= 3) {
        const momentDate = moment(selectedDate).add(1, 'd');
        setSelectedDate(momentDate.toDate());
        setAutoSelectDateErrorTimes(autoSelectDateErrorTimes + 1);
      }
    },
    retry: false,
    enabled: !!partner && visible,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (visible) {
      // 초기화
      setCurrentCalendarVisibleMonth(new Date());
      setSelectedTime(undefined);
      setStep(1);
    }
  }, [visible]);

  const getModalTitle = useCallback(() => {
    switch (step) {
      case 1:
        return strings.selectDate;
      case 2:
        return strings.requestReservation;
      default:
        return strings.selectDate;
    }
  }, [step]);

  const getMemoParam = () => {
    const chatbotChannel =
      searchParams.get('chatbotChannel') ||
      searchParams.get('chatBotChannel') ||
      undefined;
    const respondIOChannelName =
      searchParams.get('respondIOChannelName') || undefined;
    const userKey = searchParams.get('userKey') || undefined;
    const userName = searchParams.get('userName') || undefined;
    let memo = '';
    if (consultingMemo) {
      memo += consultingMemo;
    }
    if (consultingCategories.length > 0) {
      memo += `${memo ? '\n' : ''}-추가상담요청 : ${consultingCategories.join(',')}`;
    }
    if (chatbotChannel && userKey && userName) {
      memo += `${memo ? '\n\n' : ''}-RESERVATION CHANNEL : ${chatbotChannel}${
        respondIOChannelName
          ? ` (${respondIOChannelName.replace('+', '')})`
          : ''
      }\n-${chatbotChannel} USER NAME : ${userName}`;
    }
    return memo;
  };

  const makeReservation = async (token: string) => {
    setSubmitting(true);
    try {
      const reservedAt =
        moment(selectedDate).format('YYYY-MM-DD') + ` ${selectedTime}`;

      let respondIOUserKey: string | undefined;
      let reservationChannel: ReservationChannel | undefined;
      let reservationPushChannel: ChatbotChannel | undefined;
      if (isValidReservationChannel(channel)) {
        reservationChannel = channel as ReservationChannel;
      }
      const chatbotChannel =
        searchParams.get('chatbotChannel') ||
        searchParams.get('chatBotChannel') ||
        undefined;
      if (isValidReservationPushChannel(chatbotChannel)) {
        reservationPushChannel = chatbotChannel as ChatbotChannel;
        const userKey = searchParams.get('userKey') || undefined;
        if (chatbotChannel === ChatbotChannel.RESPOND_IO && userKey) {
          respondIOUserKey = userKey;
        }
      }

      const body: AddReservationBody = {
        expectPrice: 0,
        reservationMemo: getMemoParam(),
        reservedAt,
        treatmentCategoryId,
        needConsultation: isConsultingReservation,
        reservationChannel,
        ...getUtmQueries(searchParams),
        languageCode:
          languageCode === LanguageCode.ko ? undefined : languageCode,
        reservationPushChannel,
        respondIOUserKey,
      };
      const row = await addReservation(body, token);

      sendPurchaseEvent(row, []);

      const { id: reservationId } = row;
      const chatLogId = searchParams.get('chatLogId') || undefined;

      if (chatLogId && partner) {
        try {
          const chatId = parseInt(chatLogId);
          sendReservationCompleteMessage(partner.id, token, {
            reservationId,
            chatLogId: chatId,
            reservationType: 'REQUEST',
          }).then(() => {
            // Do nothing
          });
        } catch (e) {
          console.error(e);
          // Do nothing
        }
      }

      navigate(`${ROUTER_PATHS.RESERVATION_COMPLETE}${location.search}`, {
        state: {
          ...row,
          parentCategoryName,
          childCategoryName,
          reservationType,
        },
      });
    } catch (e) {
      const error = e as AppError;
      if (error.message) {
        showAlert(error.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleNext = async () => {
    if (customer) {
      await makeReservation(customer.token);
    } else {
      setAuthModalVisible(true);
    }
  };

  const getReservationDateText = () => {
    if (selectedDate && selectedTime) {
      let date = `${moment(selectedDate).tz('Asia/Seoul').format('YYYY.MM.DD. (dd)')} ${selectedTime}`;
      if (languageCode !== 'ko') {
        return `${date} (KST)`;
      }
      return date;
    }

    return '';
  };

  const partnerName = useMemo(() => getPartnerName(partner), [partner]);

  const renderContents = () => {
    if (step === 1) {
      return (
        <div
          style={{
            paddingLeft: 20,
            paddingBottom: 20,
            paddingRight: 20,
          }}
        >
          <ReservationCalendar
            value={selectedDate || undefined}
            loading={reservationAvailableDatesQuery.isLoading}
            availableDates={availableDates}
            onDateSelect={(date) => {
              setSelectedDate(date);
              setSelectedTime(undefined);
            }}
            onMonthChange={(date) => {
              setCurrentCalendarVisibleMonth(date);
            }}
          />
          <ReservationTimeSelectRadioGroup
            loading={reservationTimeBlocksQuery.isLoading}
            timeBlocks={timeBlocks}
            value={selectedTime}
            onTimeSelect={(timeText) => {
              setSelectedTime(timeText);
            }}
          />
        </div>
      );
    }

    if (step === 2) {
      return (
        <div
          className="flex flex-col h-full"
          style={{
            backgroundColor: colors.greyHover,
          }}
        >
          <div className="bg-white py-6 px-5">
            <div>
              <Typo type="m-16">{strings.reservationVerifyTitle}</Typo>
            </div>
            {!isConsultingReservationAutoAccept &&
              !isTreatmentReservationAutoAccept && (
                <Typo type="r-12" color={colors.subTitle}>
                  {customLabel?.reservationRequestNotice ||
                    strings.reservationRequestNotice}
                </Typo>
              )}

            <div
              className="flex flex-col gap-2"
              style={{
                padding: '14px 16px',
                backgroundColor: colors.greyHover,
                marginTop: 16,
                borderRadius: 12,
              }}
            >
              <Typo type="m-16">{getReservationDateText()}</Typo>
              <Typo type="r-16">{partnerName}</Typo>
            </div>
          </div>
          {treatmentCategoryId && (
            <div className="bg-white py-6 px-5">
              <Typo type="m-16">{strings.treatmentCategoryTitle}</Typo>
              <div
                className="flex flex-col gap-2"
                style={{
                  padding: '14px 16px',
                  backgroundColor: colors.greyHover,
                  marginTop: 16,
                  borderRadius: 12,
                }}
              >
                {parentCategoryName && childCategoryName && (
                  <Typo type="m-16">{`${parentCategoryName} / ${childCategoryName}`}</Typo>
                )}
                {parentCategoryName && !childCategoryName && (
                  <Typo type="m-16">{parentCategoryName}</Typo>
                )}
              </div>
            </div>
          )}
          {consultingCategories.length > 0 && (
            <div className="bg-white py-6 px-5">
              <Typo type="m-16">{strings.selectedConsultingCategoryTitle}</Typo>
              <div
                className="flex flex-col gap-2"
                style={{
                  padding: '14px 16px',
                  backgroundColor: colors.greyHover,
                  marginTop: 16,
                  borderRadius: 12,
                }}
              >
                <Typo type="m-16">{consultingCategories.join(',')}</Typo>
              </div>
            </div>
          )}
          {consultingMemo && (
            <div className="bg-white py-6 px-5">
              <Typo type="m-16">
                {customLabel?.consultingMemo || strings.consultingMemo}
              </Typo>
              <div
                className="flex flex-col gap-2"
                style={{
                  padding: '14px 16px',
                  backgroundColor: colors.greyHover,
                  marginTop: 16,
                  borderRadius: 12,
                }}
              >
                <Typo
                  type="r-16"
                  style={{
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {consultingMemo}
                </Typo>
              </div>
            </div>
          )}
        </div>
      );
    }
  };

  const renderFooterButton = () => {
    if (step === 1) {
      return (
        <Button
          disabled={!selectedDate || !selectedTime}
          style={{
            width: '100%',
          }}
          onClick={() => {
            setStep(2);
          }}
        >
          {strings.next}
        </Button>
      );
    }

    if (step === 2) {
      return (
        <Button
          disabled={submitting}
          style={{
            width: '100%',
          }}
          onClick={handleNext}
        >
          {strings.requestReservation}
        </Button>
      );
    }
  };

  return (
    <MobileModal
      visible={visible}
      onCancel={onCancel}
      title={getModalTitle()}
      backButtonAction={() => {
        if (step === 1) {
          if (onCancel) onCancel();
        } else {
          setStep(1);
        }
      }}
      footer={
        <div
          style={{
            padding: '16px 20px',
            boxShadow: '0px -4px 30px 0px #0000001f',
          }}
        >
          {renderFooterButton()}
        </div>
      }
    >
      {renderContents()}
      <AuthBottomSheet
        visible={authModalVisible}
        onCancel={() => {
          setAuthModalVisible(false);
        }}
        onAuthComplete={async (token: string) => {
          await makeReservation(token);
        }}
      />
    </MobileModal>
  );
}

export default ReservationConfirmModal;
